import React, { useState } from "react";
import {
  Autocomplete,
  Avatar,
  Box,
  Checkbox,
  Chip,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import PeopleOutlineRoundedIcon from "@mui/icons-material/PeopleOutlineRounded";

const RED = "#BF1D2D";

const AVATAR_PALETTE = [
  "#BF1D2D", "#8B5CF6", "#10B981", "#0EA5E9", "#F59E0B",
  "#EC4899", "#6366F1", "#14B8A6", "#F97316", "#0891B2",
];

const getAvatarColor = (seed = "") => {
  const str = String(seed || "U");
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) - hash + str.charCodeAt(i);
    hash |= 0;
  }
  return AVATAR_PALETTE[Math.abs(hash) % AVATAR_PALETTE.length];
};

const getInitials = (name = "") => {
  const parts = String(name || "").trim().split(/\s+/);
  if (!parts[0]) return "U";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

// Rounded, elevated popup so the list reads as a card rather than a raw menu.
const PopupPaper = (props) => (
  <Paper
    {...props}
    elevation={0}
    sx={{
      mt: 1,
      borderRadius: "14px",
      border: "1px solid #E5E7EB",
      boxShadow: "0 18px 40px rgba(17,24,39,0.14)",
      overflow: "hidden",
      "& .MuiAutocomplete-listbox": {
        px: 0.75,
        py: 0.75,
        maxHeight: 320,
        "& .MuiAutocomplete-option": {
          borderRadius: "10px",
          minHeight: "auto",
          px: 1,
          py: 0.75,
          '&[aria-selected="true"]': {
            backgroundColor: "rgba(191,29,45,0.07)",
          },
          '&[aria-selected="true"].Mui-focused': {
            backgroundColor: "rgba(191,29,45,0.11)",
          },
          "&.Mui-focused": { backgroundColor: "#F3F4F6" },
        },
      },
      "& .MuiAutocomplete-noOptions": { fontSize: 13, color: "#6B7280" },
    }}
  />
);

// Searchable recipient picker used by the messaging pages. Kept separate from
// MultiSelect.jsx on purpose so that shared component stays untouched.
export default function RecipientSelect({
  register,
  array,
  required,
  disabled = false,
  multiple = true,
  inputName = "sendTo",
  placeholder = "Search and select",
}) {
  // Single-select keeps one option object; the form value stays an array either
  // way so consumers can always map over it.
  const [selectedNames, setSelectedNames] = useState(multiple ? [] : null);
  const selectedList = multiple
    ? selectedNames || []
    : selectedNames
      ? [selectedNames]
      : [];

  // Capture react-hook-form's onChange so we can forward selections to it.
  // Without this, the local onChange below overrides register()'s onChange and
  // the registered field value never updates (stays at its default []).
  const { onChange: registerOnChange, ...registerRest } = register(inputName, {
    required: { value: required, message: "sendTo is required" },
  });

  const toFormValue = (value) =>
    multiple ? value || [] : value ? [value] : [];

  // Autocomplete hands us the value directly, so shape it like the change event
  // react-hook-form expects (it reads name/value off event.target).
  const handleChange = (_, value) => {
    setSelectedNames(value);
    if (registerOnChange) {
      registerOnChange({
        target: { name: inputName, value: toFormValue(value) },
        type: "change",
      });
    }
  };

  // Same reason as above: react-hook-form's blur handler reads the value off the
  // event, and a bare DOM blur would hand it the search box's text (wiping the
  // selection), so re-state the current selection instead.
  const handleBlur = () => {
    if (registerRest.onBlur) {
      registerRest.onBlur({
        target: { name: inputName, value: toFormValue(selectedNames) },
        type: "blur",
      });
    }
  };

  const options = Array.isArray(array) ? array : [];

  return (
    <Box className="mt-1 w-100">
      <Autocomplete
        multiple={multiple}
        fullWidth
        disableCloseOnSelect={multiple}
        disabled={disabled}
        options={options}
        value={selectedNames}
        onChange={handleChange}
        limitTags={6}
        PaperComponent={PopupPaper}
        getOptionLabel={(option) => option?.desc || String(option?.value ?? "")}
        isOptionEqualToValue={(option, value) => option?.value === value?.value}
        noOptionsText="No matches found"
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.value}>
            {multiple && (
              <Checkbox
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                checked={selected}
                disableRipple
                sx={{ mr: 0.5, p: 0.5, color: "#C4C7CE", "&.Mui-checked": { color: RED } }}
              />
            )}
            <Avatar
              sx={{
                width: 30,
                height: 30,
                mr: 1.25,
                fontSize: 11.5,
                fontWeight: 700,
                bgcolor: getAvatarColor(option?.desc),
              }}
            >
              {getInitials(option?.desc)}
            </Avatar>
            <Typography sx={{ fontSize: 14, fontWeight: selected ? 600 : 500, color: "#111827" }}>
              {option?.desc}
            </Typography>
            {!multiple && selected && (
              <CheckRoundedIcon sx={{ ml: "auto", fontSize: 18, color: RED }} />
            )}
          </li>
        )}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.value}
              size="small"
              avatar={
                <Avatar sx={{ bgcolor: getAvatarColor(option?.desc), fontSize: "10px !important", fontWeight: 700 }}>
                  {getInitials(option?.desc)}
                </Avatar>
              }
              label={option?.desc}
              sx={{
                height: 28,
                borderRadius: "8px",
                bgcolor: "rgba(191,29,45,0.07)",
                border: "1px solid rgba(191,29,45,0.22)",
                color: RED,
                fontWeight: 600,
                fontSize: 12.5,
                "& .MuiChip-avatar": { color: "#fff", width: 20, height: 20 },
                "& .MuiChip-deleteIcon": {
                  color: "rgba(191,29,45,0.55)",
                  fontSize: 17,
                  "&:hover": { color: RED },
                },
              }}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            onBlur={handleBlur}
            placeholder={
              selectedList.length && multiple ? "Add more…" : selectedList.length ? "" : placeholder
            }
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <>
                  <SearchRoundedIcon sx={{ fontSize: 20, color: "#9CA3AF", ml: 0.5, mr: 0.5 }} />
                  {params.InputProps.startAdornment}
                </>
              ),
            }}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "12px",
                bgcolor: "#fff",
                gap: "4px",
                py: "6px",
                transition: "box-shadow .2s, border-color .2s",
                "& fieldset": { borderColor: "#D0D5DD" },
                "&:hover fieldset": { borderColor: "rgba(191,29,45,0.45)" },
                "&.Mui-focused fieldset": { borderColor: RED, borderWidth: "1px" },
                "&.Mui-focused": { boxShadow: "0 0 0 3px rgba(191,29,45,0.12)" },
              },
              "& .MuiOutlinedInput-input": { fontSize: 14 },
              "& .MuiOutlinedInput-root.Mui-disabled": { bgcolor: "#F3F4F6" },
            }}
          />
        )}
      />

      {multiple && selectedList.length > 0 && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.75, color: "#6B7280" }}>
          <PeopleOutlineRoundedIcon sx={{ fontSize: 15 }} />
          <Typography sx={{ fontSize: 12 }}>
            {selectedList.length} selected
          </Typography>
        </Box>
      )}
    </Box>
  );
}
